import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const AdminUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(data.is_active ? `${u.username} aktifleştirildi` : `${u.username} pasife alındı`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Değiştirilemedi");
    } finally {
      setBusyId(null);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`${u.username} kullanıcısını silmek istediğinize emin misiniz?`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Kullanıcı silindi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    } finally {
      setBusyId(null);
    }
  };

  const pending = users.filter((u) => u.role !== "admin" && !u.is_active).length;

  return (
    <div data-testid="admin-users-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <Users className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-bold">Kullanıcı Yönetimi</h3>
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{users.length} kullanıcı</span>
        {pending > 0 && (
          <span className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }}>
            {pending} onay bekliyor
          </span>
        )}
      </div>

      <div className="px-2 sm:px-3 py-2">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>Henüz kullanıcı yok</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  <th className="text-left px-3 py-2">Kullanıcı</th>
                  <th className="text-left px-3 py-2">Rol</th>
                  <th className="text-left px-3 py-2">BingX</th>
                  <th className="text-left px-3 py-2">Durum</th>
                  <th className="text-right px-3 py-2">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.username}`} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                    <td className="px-3 py-3 font-semibold">{u.username}</td>
                    <td className="px-3 py-3">
                      {u.role === "admin"
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(59,130,246,0.15)", color: "#60A5FA" }}><ShieldCheck className="w-3 h-3" /> admin</span>
                        : <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>kullanıcı</span>}
                    </td>
                    <td className="px-3 py-3">
                      {u.bingx_configured
                        ? <span className="text-[11px]" style={{ color: "#10B981" }}>Bağlı</span>
                        : <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>
                    <td className="px-3 py-3">
                      {u.is_active
                        ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(16,185,129,0.15)", color: "#10B981" }}>Aktif</span>
                        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }}>Pasif</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {u.role !== "admin" && (
                          <>
                            <button
                              data-testid={`toggle-active-${u.username}`}
                              onClick={() => toggleActive(u)}
                              disabled={busyId === u.id}
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40"
                              style={{ background: u.is_active ? "#10B981" : "var(--border-subtle)" }}
                              title={u.is_active ? "Pasife al" : "Aktifleştir"}
                            >
                              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform" style={{ transform: u.is_active ? "translateX(22px)" : "translateX(4px)" }} />
                            </button>
                            <button data-testid={`delete-user-${u.username}`} onClick={() => removeUser(u)} disabled={busyId === u.id}
                              className="p-1.5 rounded-lg transition-all hover:brightness-110 border" style={{ borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
